import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import IntegrationsForm from "./IntegrationsForm";
import * as organizerThunks from "../../../redux/thunks/organizerThunks";
import * as revaThunks from "../../../redux/thunks/revaThunks";
import * as tournamentThunks from "../../../redux/thunks/tournamentThunks";
import * as teamThunks from "../../../redux/thunks/teamThunks";
import lineupService from "../../../redux/reducers/lineupReducer/lineupService";
import { actions as alertActions } from "../../../redux/reducers/alertReducer";
import organizerActions from "../../../redux/reducers/organizerReducer/organizerActions";

const IntegrationsContainer = ({
    organizer,
    setOrganizerKeyValue,
    clearSingleOrganizer,
    updateRevaApiKey,
    loadOrganizer,
    validateRevaApiKey,
    loadAllTournaments,
    loadAllTeams,
    batchSyncLineups,
    hideSpecificAlert,
    allTournaments,
    allTeams,
    revaLineups,
    loadRevaLineups,
    loadRevaMe,
    revaConnectionError,
    match,
    loading,
}) => {
    const organizerId = match.params.organizerId;
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [selectedTournaments, setSelectedTournaments] = useState([]);
    const [lineupsByTournament, setLineupsByTournament] = useState({});

    useEffect(() => {
        clearSingleOrganizer();
        loadOrganizer(organizerId);
        loadAllTournaments(0, 100, organizerId);
        loadAllTeams(organizerId, 0, 500);
        loadRevaLineups();
    }, [organizerId]);

    const [hasCheckedConnection, setHasCheckedConnection] = useState(false);
    useEffect(() => {
        if (organizer?.revaApiKey && !hasCheckedConnection) {
            loadRevaMe();
            setHasCheckedConnection(true);
        }
    }, [organizer?.revaApiKey, hasCheckedConnection]);

    // Fetch lineups when tournaments are loaded
    useEffect(() => {
        if (allTournaments.length > 0) {
            allTournaments.forEach(async (tournament) => {
                if (!lineupsByTournament[tournament.id]) {
                    const [error, lineups] = await lineupService.loadAll(organizerId, tournament.id);
                    if (!error) {
                        setLineupsByTournament(prev => ({
                            ...prev,
                            [tournament.id]: lineups
                        }));
                    }
                }
            });
        }
    }, [allTournaments]);

    const saveIntegrationsHandler = async () => {
        const apiKey = organizer.revaApiKey;
        if (apiKey) {
            const [error] = await validateRevaApiKey(apiKey);
            if (!error) {
                updateRevaApiKey(organizerId);
                setShowSyncModal(true);
            }
        } else {
            updateRevaApiKey(organizerId);
        }
    };

    const handleSync = () => {
        // Collect all lineups from selected tournaments
        const lineupsToSync = [];
        allTournaments
            .filter(t => selectedTournaments.includes(t.id))
            .forEach(t => {
                const tournamentLineups = lineupsByTournament[t.id] || [];
                tournamentLineups.forEach(lineup => {
                    // We sync EVERY lineup. 
                    // No deduplication by Team ID because the same team can be in multiple tournaments.
                    lineupsToSync.push({
                        ...lineup,
                        tournament_id: t.id,
                        tournament_name: t.name
                    });
                });
            });

        if (lineupsToSync.length > 0) {
            batchSyncLineups(lineupsToSync);
        }
        setShowSyncModal(false);
    };

    const panelTitle = organizer.name;

    return (
        <IntegrationsForm
            setOrganizerKeyValue={setOrganizerKeyValue}
            saveIntegrations={saveIntegrationsHandler}
            hideSpecificAlert={hideSpecificAlert}
            organizer={organizer}
            loadingSave={loading.updateOrganizer || loading.validateKey}
            loadingLoad={loading.loadOrganizer}
            panelTitle={panelTitle}
            showSyncModal={showSyncModal}
            setShowSyncModal={setShowSyncModal}
            allTournaments={allTournaments}
            selectedTournaments={selectedTournaments}
            setSelectedTournaments={setSelectedTournaments}
            onSync={handleSync}
            loadingSync={loading.batchSync}
            lineupsByTournament={lineupsByTournament}
            revaLineups={revaLineups}
            revaConnectionError={revaConnectionError}
        />
    );
};

const mapState = (state) => ({
    organizer: state.organizer.single,
    allTournaments: state.tournament.all,
    allTeams: state.team.all,
    revaLineups: state.reva.lineups,
    revaConnectionError: state.reva.connectionError,
    loading: {
        ...state.loading.organizer,
        ...state.loading.reva,
        ...state.loading.tournament,
        ...state.loading.team,
    },
});

const mapDispatch = (dispatch) => ({
    hideSpecificAlert: () => dispatch(alertActions.hideSpecificAlert()),
    setOrganizerKeyValue: (key, value) =>
        dispatch(organizerActions.setOrganizerKeyValue(key, value)),
    clearSingleOrganizer: () => dispatch(organizerActions.clearSingleOrganizer()),
    loadOrganizer: (organizerId) =>
        dispatch(organizerThunks.loadOrganizer(organizerId)),
    updateRevaApiKey: (organizerId) =>
        dispatch(organizerThunks.updateRevaApiKey(organizerId)),
    validateRevaApiKey: (apiKey) => dispatch(revaThunks.validateRevaApiKey(apiKey)),
    loadRevaMe: () => dispatch(revaThunks.loadRevaMe()),
    loadRevaLineups: () => dispatch(revaThunks.loadRevaLineups()),
    loadAllTournaments: (page, pageSize, organizerId) =>
        dispatch(tournamentThunks.loadAllTournaments(page, pageSize, organizerId)),
    loadAllTeams: (organizerId, page, pageSize) =>
        dispatch(teamThunks.loadAllTeams(organizerId, page, pageSize)),
    batchSyncLineups: (lineups) => dispatch(revaThunks.batchSyncLineups(lineups)),
});

export default withRouter(connect(mapState, mapDispatch)(IntegrationsContainer));
